import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../baseurl";

const CartContext = createContext();

/* ------------------------------------------------------------------ */
/* Inline toast — mirrors the styling of the shared Toast component     */
/* ------------------------------------------------------------------ */
const toastIcons = {
  success: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M3 17l6-6 4 4 8-8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M15 7h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  error: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  info: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M12 11v5M12 8v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  warning: (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 3l9 16H3l9-16z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M12 10v4M12 17v.01" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
  close: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  ),
};

const toastVariants = {
  success: { icon: "bg-teal-500/15 text-teal-400", bar: "bg-teal-500" },
  error: { icon: "bg-red-500/15 text-red-400", bar: "bg-red-500" },
  info: { icon: "bg-gray-400/15 text-gray-300", bar: "bg-gray-400" },
  warning: { icon: "bg-amber-500/15 text-amber-400", bar: "bg-amber-400" },
};

function CartToast({ id, title, description, variant = "info", duration = 4000, onDismiss }) {
  const [leaving, setLeaving] = useState(false);
  const [progress, setProgress] = useState(100);
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const startRef = useRef(Date.now());
  const remainingRef = useRef(duration);

  const style = toastVariants[variant] ?? toastVariants.info;

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => onDismiss(id), 200);
  }, [id, onDismiss]);

  const clearTimers = () => {
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
  };

  const start = useCallback(() => {
    startRef.current = Date.now();
    timeoutRef.current = setTimeout(dismiss, remainingRef.current);
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const pct = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(pct);
    }, 30);
  }, [dismiss, duration]);

  const pause = () => {
    clearTimers();
    remainingRef.current -= Date.now() - startRef.current;
  };

  useEffect(() => {
    start();
    return clearTimers;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="status"
      onMouseEnter={pause}
      onMouseLeave={start}
      className={`relative w-80 overflow-hidden rounded-2xl border border-white/10 bg-black/70 backdrop-blur-md shadow-lg
        transition-all duration-200 ease-out
        ${leaving ? "opacity-0 translate-x-2" : "opacity-100 translate-x-0 animate-cart-toast-in"}`}
    >
      <div className="flex items-start gap-3 p-4 pr-9">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${style.icon}`}>
          {toastIcons[variant]}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          {title && <p className="text-sm font-semibold text-white">{title}</p>}
          {description && (
            <p className={`text-xs text-gray-400 ${title ? "mt-0.5" : ""}`}>{description}</p>
          )}
        </div>
        <button
          onClick={dismiss}
          aria-label="Dismiss notification"
          className="absolute right-3 top-3 text-gray-500 transition-colors hover:text-gray-300"
        >
          {toastIcons.close}
        </button>
      </div>

      <div className="h-0.5 w-full bg-white/10">
        <div
          className={`h-full ${style.bar} transition-[width] duration-100 ease-linear`}
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}

function CartToastContainer({ toasts, onDismiss }) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col items-end gap-2">
      {toasts.map((t) => (
        <div key={t.id} className="pointer-events-auto">
          <CartToast {...t} onDismiss={onDismiss} />
        </div>
      ))}

      <style>{`
        @keyframes cart-toast-in {
          from { opacity: 0; transform: translateY(8px) translateX(8px); }
          to { opacity: 1; transform: translateY(0) translateX(0); }
        }
        .animate-cart-toast-in { animation: cart-toast-in 0.25s ease-out; }
      `}</style>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Cart provider                                                        */
/* ------------------------------------------------------------------ */
export function CartProvider({ children }) {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((opts) => {
    const id = opts.id ?? crypto.randomUUID();
    setToasts((prev) => [...prev, { id, variant: "info", duration: 4000, ...opts }]);
    return id;
  }, []);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const mapCartItems = (items) =>
    items.map((entry) => ({
      id: entry.item._id,
      name: entry.item.title,
      price: entry.item.price,
      image: entry.item.image
        ? `${BASE_URL.replace(/\/api$/, "")}${entry.item.image}`
        : null,
      quantity: entry.quantity,
    }));

  const fetchCart = async () => {
    const token = localStorage.getItem("token");
    if (!token) return;

    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/carts`, {
        headers: getAuthHeader(),
      });
      setCartItems(mapCartItems(res.data.cart.items || []));
    } catch (err) {
      console.error("Failed to fetch cart:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, []);

  const addToCart = async (product) => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast({
        variant: "error",
        title: "Please sign in",
        description: "You need to be logged in to add items to your cart.",
      });
      return;
    }

    const existing = cartItems.find((item) => item.id === product._id);
    if (existing) {
      toast({
        variant: "info",
        title: "Already in cart",
        description: `${product.title} is already in your cart.`,
      });
      openCart();
      return;
    }

    try {
      const res = await axios.post(
        `${BASE_URL}/carts`,
        { itemId: product._id },
        { headers: getAuthHeader() }
      );
      setCartItems(mapCartItems(res.data.cart.items || []));
      openCart();
      toast({
        variant: "success",
        title: "Added to cart",
        description: `${product.title} has been added to your cart.`,
      });
    } catch (err) {
      toast({
        variant: "error",
        title: "Couldn't add to cart",
        description:
          err.response?.data?.message || "Something went wrong while adding the item",
      });
    }
  };

  const removeFromCart = async (id) => {
    try {
      const res = await axios.delete(`${BASE_URL}/carts/${id}`, {
        headers: getAuthHeader(),
      });
      setCartItems(mapCartItems(res.data.cart.items || []));
    } catch (err) {
      toast({
        variant: "error",
        title: "Couldn't remove item",
        description:
          err.response?.data?.message || "Something went wrong while removing the item",
      });
    }
  };

  const updateQuantity = async (id, delta) => {
    try {
      const res = await axios.patch(
        `${BASE_URL}/carts/${id}`,
        { delta },
        { headers: getAuthHeader() }
      );
      setCartItems(mapCartItems(res.data.cart.items || []));
    } catch (err) {
      toast({
        variant: "error",
        title: "Couldn't update quantity",
        description:
          err.response?.data?.message || "Something went wrong while updating quantity",
      });
    }
  };

  

  const cartCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        loading,
        openCart,
        closeCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        setIsCartOpen,
        cartCount,
        subtotal,
        refreshCart: fetchCart,
      }}
    >
      {children}
      <CartToastContainer toasts={toasts} onDismiss={dismissToast} />
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}